import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
    Optional,
    output,
    Self,
    signal,
    ViewEncapsulation
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NgControl } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';

@Component({
    selector: 'fcp-checkbox',
    imports: [CommonModule, CheckboxModule, FormsModule],
    styleUrl: './fcp-checkbox.component.less',
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'fcp-checkbox-wrapper',
        '[class.fcp-checkbox-wrapper--small]': 'size() === "small"',
        '[attr.data-e2e-hook]': 'e2e()'
    },
    template: `
        <p-checkbox
            [ngModel]="model()"
            [value]="value()"
            [name]="name()"
            [binary]="binary()"
            [ariaLabelledBy]="ariaLabelledBy()"
            [ariaLabel]="ariaLabel()"
            [tabindex]="tabindex()"
            [inputId]="inputId()"
            [indeterminate]="indeterminate()"
            [size]="size()"
            [disabled]="isDisabled()"
            [readonly]="readonly()"
            [required]="required()"
            [autofocus]="autofocus()"
            [trueValue]="trueValue()"
            [falseValue]="falseValue()"
            [variant]="variant()"
            [styleClass]="checkboxStyleClass()"
            (ngModelChange)="handleModelChange($event)"
            (onChange)="handleOnChange($event)"
            (onFocus)="onFocus.emit($event)"
            (onBlur)="handleBlur($event)"
        />
        <div class="fcp-checkbox-wrapper-labels">
            @if (label()) {
                <label [class]="primaryLabelClasses()" [for]="inputId()">{{ label() }}</label>
                @if (additionalLabel()) {
                    <label class="fcp-checkbox-wrapper-labels-additional">{{ additionalLabel() }}</label>
                }
            }
        </div>
    `
})
export class FcpCheckboxComponent implements ControlValueAccessor {
    e2e = input<string | null>(null);
    value = input<unknown>();
    name = input<string>();
    binary = input<boolean>(false);
    ariaLabelledBy = input<string>();
    ariaLabel = input<string>();
    tabindex = input<number>();
    inputId = input<string | null>(null);
    indeterminate = input<boolean>(false);
    size = input<CheckboxSize>();
    disabled = input<boolean>(false);
    required = input<boolean>(false);
    readonly = input<boolean>(false);
    autofocus = input<boolean>(false);
    trueValue = input<unknown>(true);
    falseValue = input<unknown>(false);
    variant = input<CheckboxVariant>();

    label = input<string>();
    additionalLabel = input<string>();

    onChange = output<CheckboxChangeEvent>();
    onFocus = output<Event>();
    onBlur = output<Event>();

    constructor(@Optional() @Self() private readonly ngControl: NgControl | null) {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
    }

    private _formDisabled = signal<boolean>(false);

    protected model = signal<unknown>(null);
    protected formOnChange: (value: unknown) => void = () => {};
    protected formOnTouched: () => void = () => {};

    isUnchecked = computed(() => this.model() === this.falseValue());
    isDisabled = computed(() => this.disabled() || this._formDisabled());
    primaryLabelClasses = computed(
        () => (this.isDisabled() ? 'disabled' : '') + ' fcp-checkbox-wrapper-labels-primary'
    );

    indeterminateStyleClass = computed(() =>
        this.indeterminate() ? `is-indeterminate${this.isUnchecked() ? ' is-indeterminate-unchecked' : ''}` : ''
    );
    checkboxStyleClass = computed(() => this.indeterminateStyleClass());

    writeValue(value: unknown): void {
        this.model.set(value);
    }

    registerOnChange(fn: (value: unknown) => void): void {
        this.formOnChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.formOnTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this._formDisabled.set(isDisabled);
    }

    protected handleOnChange(event: CheckboxChangeEvent): void {
        if (this.indeterminate()) {
            event.checked = false;
        }

        this.onChange.emit(event);
    }

    protected handleModelChange(value: unknown): void {
        const finalValue = this.indeterminate() ? this.falseValue() : value;

        this.model.set(finalValue);
        this.formOnChange(finalValue);
    }

    protected handleBlur(event: Event): void {
        this.onBlur.emit(event);
        this.deferTouched();
    }

    /** Defer so value updates can run before touched. */
    protected deferTouched(): void {
        queueMicrotask(() => this.formOnTouched());
    }
}

export enum CheckboxSize {
    Small = 'small',
    Normal = null,
    Large = 'large'
}

export enum CheckboxVariant {
    Filled = 'filled',
    Outlined = 'outlined'
}

export interface CheckboxChangeEvent {
    checked: unknown;
    originalEvent: Event;
}
